import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useSpeechRecognition — thin wrapper around the browser Web Speech API.
 * Returns { supported, listening, interim, start, stop, reset, error }.
 *
 * `onResult(finalText, isFinal)` fires every time a chunk is recognised.
 * Pass `continuous: true` to keep listening until stop(). Default: stops on first final.
 */
export function useSpeechRecognition({ onResult, lang = "en-US", continuous = true } = {}) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const onResultRef = useRef(onResult);

  // Keep latest callback without resetting recognition
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) {
      setSupported(false);
      return;
    }
    setSupported(true);
    const rec = new SR();
    rec.continuous = continuous;
    rec.interimResults = true;
    rec.lang = lang;

    rec.onresult = (event) => {
      let finalText = "";
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interimText += r[0].transcript;
      }
      if (finalText) {
        onResultRef.current?.(finalText.trim(), true);
        setInterim("");
      } else {
        setInterim(interimText);
      }
    };

    rec.onerror = (e) => {
      setError(e?.error || "speech-error");
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
      setInterim("");
    };

    recognitionRef.current = rec;
    return () => {
      try { 
        rec.stop(); 
      } catch (err) {
        console.error("Speech recognition cleanup error:", err);
      }
      recognitionRef.current = null;
    };
  }, [lang, continuous]);

  const start = useCallback(() => {
    const rec = recognitionRef.current;
    if (!rec || listening) return;
    setError(null);
    setInterim("");
    try {
      rec.start();
      setListening(true);
    } catch (e) {
      // start() can throw if already running
      console.error("Speech recognition start error:", e);
      setError("start-failed");
    }
  }, [listening]);

  const stop = useCallback(() => {
    const rec = recognitionRef.current;
    if (!rec) return;
    try { 
      rec.stop(); 
    } catch (err) {
      console.error("Speech recognition stop error:", err);
    }
    setListening(false);
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setInterim("");
  }, []);

  return { supported, listening, interim, error, start, stop, reset };
}
